/**
 * Sync engine — drains the pending_submissions queue.
 *
 * Source of truth: Dexie pending_submissions. Engine wakes on
 *   - app start (kickEngine called from main.tsx)
 *   - every 30s while the doc is visible (foreground heartbeat)
 *   - 'online' / 'focus' / 'visibilitychange' events
 *   - explicit kickEngine() from form submits
 *   - Background Sync API replay (Android Chrome only, fired by SW)
 *
 * Per-row lifecycle:
 *   queued  →  syncing  →  synced            (2xx)
 *   queued  →  syncing  →  queued + backoff  (network error)
 *   queued  →  syncing  →  failed            (4xx; needs user action)
 *
 * Backoff is exponential per row: 5s, 30s, 5m, 30m cap. Retries are
 * idempotent because the same client_submission_id is sent each time.
 *
 * Authorization is the in-memory bearer set by setBearerToken() —
 * engine no-ops when locked.
 */
import { apiClient, HttpError, getBearerToken } from '../api/client';
import { getDb } from '../db';
import type { PendingSubmission } from '../db';
import { logEvent } from './log';
import { uploadPendingPhotos } from './photo-upload';

// ── Status pub-sub ─────────────────────────────────────────────────────

export type EngineState = 'idle' | 'syncing' | 'failed' | 'offline';

interface EngineSnapshot {
  state: EngineState;
  queued: number;
  failed: number;
  last_sync_at: number | null;
  last_error: string | null;
}

let _snapshot: EngineSnapshot = {
  state: 'idle',
  queued: 0,
  failed: 0,
  last_sync_at: null,
  last_error: null,
};

const _listeners = new Set<(s: EngineSnapshot) => void>();

export function getEngineSnapshot(): EngineSnapshot {
  return _snapshot;
}

export function subscribeEngine(fn: (s: EngineSnapshot) => void): () => void {
  _listeners.add(fn);
  fn(_snapshot);
  return () => _listeners.delete(fn);
}

function publish(patch: Partial<EngineSnapshot>): void {
  _snapshot = { ..._snapshot, ...patch };
  for (const fn of _listeners) {
    try {
      fn(_snapshot);
    } catch {
      /* swallow */
    }
  }
}

// ── Backoff ────────────────────────────────────────────────────────────

const BACKOFF_STEPS_MS = [5_000, 30_000, 300_000, 1_800_000]; // 5s, 30s, 5m, 30m

function backoffFor(attempts: number): number {
  if (attempts <= 0) return 0;
  return BACKOFF_STEPS_MS[Math.min(attempts - 1, BACKOFF_STEPS_MS.length - 1)];
}

// ── Engine state ───────────────────────────────────────────────────────

let _running = false;
let _wakeRequested = false;
let _heartbeatId: number | null = null;

/** Request a drain pass. Coalesces — repeated kicks while a drain is
 *  in flight set a "do another pass after this one" flag rather than
 *  starting a parallel drain. */
export function kickEngine(): void {
  if (_running) {
    _wakeRequested = true;
    return;
  }
  void runDrain();
}

async function runDrain(): Promise<void> {
  if (_running) {
    _wakeRequested = true;
    return;
  }
  _running = true;
  _wakeRequested = false;
  try {
    await refreshCounts();
    if (!getBearerToken()) {
      // Locked — no point trying.
      publish({ state: navigator.onLine ? 'idle' : 'offline' });
      return;
    }
    if (!navigator.onLine) {
      publish({ state: 'offline' });
      return;
    }
    publish({ state: 'syncing' });
    await drainOnce();
    // Second pass: any FieldRecord submissions that synced with photos
    // still pending get their bytes uploaded.
    if (navigator.onLine) {
      try {
        await uploadPendingPhotos();
      } catch (err) {
        await logEvent('photo_upload.pass_error', {
          error: (err as Error).message,
        });
      }
    }
    await refreshCounts();
    const snap = _snapshot;
    if (snap.failed > 0) {
      publish({ state: 'failed' });
    } else if (snap.queued > 0) {
      // Still rows queued for backoff; transition to idle and let the
      // next heartbeat / online event try again.
      publish({ state: 'idle' });
    } else {
      publish({ state: 'idle', last_sync_at: Date.now(), last_error: null });
    }
  } finally {
    _running = false;
    if (_wakeRequested) {
      _wakeRequested = false;
      void runDrain();
    }
  }
}

async function drainOnce(): Promise<void> {
  const db = getDb();
  // Grab queued rows whose backoff has elapsed. We sort by created_at
  // so older submissions go first (FIFO under retry constraints).
  const now = Date.now();
  const candidates = await db.pending_submissions
    .where('status')
    .equals('queued')
    .toArray();

  // Filter out rows still in backoff window.
  const ready: PendingSubmission[] = [];
  for (const row of candidates) {
    const bo = backoffFor(row.attempts);
    const last = row.last_attempt_at ?? 0;
    if (last + bo > now) continue;
    ready.push(row);
  }
  // Stable sort by created_at to keep FIFO order under the post-filter list.
  ready.sort((a, b) => a.created_at - b.created_at);

  for (const row of ready) {
    if (!navigator.onLine) {
      publish({ state: 'offline' });
      return;
    }
    await attemptOne(row);
  }
}

async function attemptOne(row: PendingSubmission): Promise<void> {
  const db = getDb();
  await db.pending_submissions.update(row.client_submission_id, {
    status: 'syncing',
    last_attempt_at: Date.now(),
    attempts: row.attempts + 1,
  });

  try {
    const resp = await apiClient.postJson<unknown, unknown>(row.endpoint, row.payload);
    const server_id = extractServerId(resp);
    await db.pending_submissions.update(row.client_submission_id, {
      status: 'synced',
      last_error: null,
      server_id,
      // Mark for photo-upload pass when this is a field-record write
      // and we have a server_id to attach photos under.
      photos_pending: shouldQueuePhotos(row.endpoint, server_id),
    });
    await logEvent('submit.synced', {
      client_submission_id: row.client_submission_id,
      endpoint: row.endpoint,
      attempts: row.attempts + 1,
      server_id,
    });
  } catch (err) {
    if (err instanceof HttpError) {
      // 4xx → needs user action. Don't keep retrying a server-rejected row.
      const isRetryable = err.status >= 500 || err.status === 429;
      if (isRetryable) {
        await db.pending_submissions.update(row.client_submission_id, {
          status: 'queued',
          last_error: trunc(`HTTP ${err.status}`, 500),
        });
        await logEvent('submit.retry', {
          client_submission_id: row.client_submission_id,
          status: err.status,
          attempt: row.attempts + 1,
        });
      } else {
        await db.pending_submissions.update(row.client_submission_id, {
          status: 'failed',
          last_error: trunc(`HTTP ${err.status}: ${stringifyBody(err.body)}`, 500),
        });
        await logEvent('submit.failed', {
          client_submission_id: row.client_submission_id,
          status: err.status,
          attempt: row.attempts + 1,
        });
      }
    } else {
      // Network / abort — retryable.
      await db.pending_submissions.update(row.client_submission_id, {
        status: 'queued',
        last_error: trunc((err as Error).message ?? String(err), 500),
      });
      await logEvent('submit.retry', {
        client_submission_id: row.client_submission_id,
        attempt: row.attempts + 1,
        error: (err as Error).message,
      });
    }
  }
}

function trunc(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

/** Extract server-side `id` from a JSON response. Best-effort —
 *  endpoints that don't return one just produce null. */
function extractServerId(resp: unknown): string | null {
  if (resp == null || typeof resp !== 'object') return null;
  const id = (resp as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

const PHOTO_PARENT_ENDPOINT_RE = /\/sampling\/events\/[^/]+\/field-records$/;
function shouldQueuePhotos(endpoint: string, server_id: string | null): boolean {
  return !!server_id && PHOTO_PARENT_ENDPOINT_RE.test(endpoint);
}

function stringifyBody(b: unknown): string {
  if (b == null) return '';
  if (typeof b === 'string') return b.slice(0, 200);
  try {
    return JSON.stringify(b).slice(0, 200);
  } catch {
    return String(b);
  }
}

async function refreshCounts(): Promise<void> {
  const db = getDb();
  const [queued, failed] = await Promise.all([
    db.pending_submissions.where('status').anyOf('queued', 'syncing').count(),
    db.pending_submissions.where('status').equals('failed').count(),
  ]);
  publish({ queued, failed });
}

// ── Heartbeat + lifecycle wiring ───────────────────────────────────────

const HEARTBEAT_MS = 30_000;

export function startEngine(): void {
  // Idempotent — calling twice doesn't double-schedule.
  if (_heartbeatId != null) return;

  // Heartbeat while doc is visible (the iOS Background Sync fallback).
  const tick = () => {
    if (document.visibilityState === 'visible') kickEngine();
  };
  _heartbeatId = window.setInterval(tick, HEARTBEAT_MS) as unknown as number;

  window.addEventListener('online', kickEngine);
  window.addEventListener('focus', kickEngine);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kickEngine();
  });

  // Initial pass.
  kickEngine();
}

/** Register a Background Sync task with the SW. Android Chrome supports
 *  this; iOS Safari ignores it silently — that's fine, the foreground
 *  heartbeat covers iOS. */
export async function triggerBackgroundSync(): Promise<void> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register(tag: string): Promise<void> };
    };
    if (reg.sync && typeof reg.sync.register === 'function') {
      await reg.sync.register('field-submissions');
    }
  } catch {
    // Background Sync not supported — no-op. Heartbeat handles it.
  }
}
